import type { Editor } from "tinymce";

export type MentionPerson = { id: string; name: string; label: string };

/** What the picker reads from the controller that owns it, looked up at each use because targets come and go with Turbo. */
export interface MentionPickerHost {
    editor(): Editor | null;
    editable(): HTMLElement | null;
    dialog(): HTMLDialogElement | null;
    search(): HTMLInputElement;
    options(): HTMLUListElement;
    empty(): HTMLElement;
    people(): MentionPerson[];
}

/**
 * The `@` Mention picker beside a rich-text editor: a dialog listing the People that match what
 * follows the `@`, chosen with the keyboard or the pointer.
 */
export class MentionPicker {
    private mentionRange: Range | null = null;
    private activeIndex = 0;

    constructor(private readonly host: MentionPickerHost) {}

    public get isOpen(): boolean {
        return this.host.dialog()?.open === true;
    }

    public onSearch(): void {
        this.renderOptions(this.host.search().value);
    }

    public onSearchKeydown(event: KeyboardEvent): void {
        this.handleKeydown(event, { focusEditableOnEscape: true });
    }

    public onEditorInput(): void {
        const editable = this.host.editable();
        if (!editable || !this.enabled()) return;

        const found = findMentionAtCaret(editable);
        if (!found) {
            this.close();
            return;
        }

        this.mentionRange = found.range;
        this.host.search().value = found.query;
        this.open();
        this.renderOptions(found.query);
    }

    public onEditorKeydown(event: KeyboardEvent): void {
        if (!this.enabled() || !this.isOpen) return;
        this.handleKeydown(event, { requireActiveForEnter: true });
    }

    public close(): void {
        this.mentionRange = null;
        this.activeIndex = 0;
        const dialog = this.host.dialog();
        if (dialog?.open) {
            dialog.close();
        }
    }

    private enabled(): boolean {
        return this.host.people().length > 0 && this.host.dialog() !== null;
    }

    private handleKeydown(
        event: KeyboardEvent,
        options: { focusEditableOnEscape?: boolean; requireActiveForEnter?: boolean } = {},
    ): void {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            this.close();
            if (options.focusEditableOnEscape) this.host.editor()?.focus();
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            this.moveActive(1);
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            this.moveActive(-1);
            return;
        }

        if (event.key === "Enter") {
            if (options.requireActiveForEnter && !this.host.options().querySelector("[data-active='true']"))
                return;
            event.preventDefault();
            this.selectActive();
        }
    }

    private open(): void {
        const dialog = this.host.dialog();
        if (!dialog) return;
        if (!dialog.open) {
            dialog.show();
            // Keep caret in the editor so continued typing updates the @query.
            this.host.editor()?.focus();
        }
    }

    private renderOptions(query: string): void {
        const needle = query.trim().toLowerCase();
        const matches = this.host.people().filter((person) => {
            if (!needle) return true;
            return person.label.toLowerCase().includes(needle) || person.name.toLowerCase().includes(needle);
        });

        const options = this.host.options();
        options.replaceChildren();
        this.activeIndex = 0;

        matches.forEach((person, index) => {
            const li = document.createElement("li");
            li.className = "cursor-pointer px-3 py-2 hover:bg-neutral-100 data-[active=true]:bg-neutral-100";
            li.textContent = person.label;
            li.dataset.id = person.id;
            li.dataset.name = person.name;
            li.dataset.active = index === 0 ? "true" : "false";
            li.addEventListener("mousedown", (event) => {
                event.preventDefault();
                this.insertMention(person.id, person.name);
            });
            options.appendChild(li);
        });

        this.host.empty().classList.toggle("hidden", matches.length > 0);
    }

    private moveActive(delta: number): void {
        const items = Array.from(this.host.options().querySelectorAll<HTMLElement>("li"));
        if (items.length === 0) return;
        this.activeIndex = (this.activeIndex + delta + items.length) % items.length;
        items.forEach((item, index) => {
            item.dataset.active = index === this.activeIndex ? "true" : "false";
        });
        items[this.activeIndex]?.scrollIntoView({ block: "nearest" });
    }

    private selectActive(): void {
        const active = this.host.options().querySelector<HTMLElement>("[data-active='true']");
        if (!active?.dataset.id || !active.dataset.name) return;
        this.insertMention(active.dataset.id, active.dataset.name);
    }

    private insertMention(personId: string, fullName: string): void {
        const range = this.mentionRange;
        const editor = this.host.editor();
        if (!range || !editor) return;

        editor.selection.setRng(range);
        const markup =
            `<span class="mention" data-person-id="${escapeHtml(personId)}">@${escapeHtml(fullName)}</span>&nbsp;`;
        editor.insertContent(markup);
        this.close();
    }
}

function findMentionAtCaret(editable: HTMLElement): { query: string; range: Range } | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;

    const caret = selection.getRangeAt(0);
    if (!editable.contains(caret.startContainer)) return null;

    const node = caret.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return null;

    const text = node.textContent ?? "";
    const offset = caret.startOffset;
    const before = text.slice(0, offset);
    const at = before.lastIndexOf("@");
    if (at < 0) return null;

    // @ must start a token (start or whitespace before it); no space after @ until pick.
    if (at > 0 && !/\s/.test(before[at - 1]!)) return null;

    const query = before.slice(at + 1);
    if (/\s/.test(query)) return null;

    const range = document.createRange();
    range.setStart(node, at);
    range.setEnd(node, offset);
    return { query, range };
}

function escapeHtml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}
